import styled from 'styled-components'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`

const PanelHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e1e5e9;
  background: #f6f8fa;
`

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #24292f;
`

const PropertiesContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #d0d7de #f6f8fa;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f6f8fa;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d7de;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #afb8c1;
  }
`

const PropertySection = styled.div`
  border-bottom: 1px solid #e1e5e9;
`

const SectionHeader = styled.div<{ isCollapsed: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f6f8fa;
  border-bottom: ${props => (props.isCollapsed ? 'none' : '1px solid #e1e5e9')};

  &:hover {
    background: #f1f3f6;
  }
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #656d76;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CollapseIcon = styled.span<{ isCollapsed: boolean }>`
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: ${props =>
    props.isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
`

const SectionContent = styled.div<{ isCollapsed: boolean }>`
  padding: ${props => (props.isCollapsed ? '0 16px' : '16px')};
  max-height: ${props => (props.isCollapsed ? '0' : '1000px')};
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${props => (props.isCollapsed ? '0' : '1')};
`

const PropertyGroup = styled.div`
  margin-bottom: 16px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #24292f;
`

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  font-size: 12px;
  background: white;

  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  font-size: 12px;
  background: white;
  resize: vertical;
  min-height: 60px;

  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
  }
`

const ColorInput = styled.input.attrs({ type: 'color' })`
  width: 40px;
  height: 32px;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
  background: white;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 2px;
  }
`

const RangeInput = styled.input.attrs({ type: 'range' })`
  width: 100%;
  margin: 8px 0;
`

const RangeValue = styled.span`
  font-size: 12px;
  color: #656d76;
  float: right;
`

const Button = styled.button<{ variant?: 'danger' }>`
  padding: 8px 12px;
  border: 1px solid
    ${props => (props.variant === 'danger' ? '#da3633' : '#d0d7de')};
  border-radius: 4px;
  background: ${props => (props.variant === 'danger' ? '#da3633' : 'white')};
  color: ${props => (props.variant === 'danger' ? 'white' : '#24292f')};
  font-size: 12px;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: ${props =>
      props.variant === 'danger' ? '#b92f2c' : '#f6f8fa'};
  }

  &:active {
    transform: translateY(1px);
  }
`

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #656d76;
  font-size: 14px;
`

const EmptyStateIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  font-size: 12px;
  background: white;

  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
  }
`

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  margin-right: 8px;
`

const InfoText = styled.div`
  font-size: 11px;
  color: #656d76;
  margin-top: 4px;
  font-style: italic;
`

const FileInputWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: inline-block;
  width: 100%;
`

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f6f8fa;
  border: 2px dashed #d0d7de;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #24292f;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f6;
    border-color: #0969da;
    color: #0969da;
  }
`

const HiddenFileInput = styled.input.attrs({ type: 'file' })`
  position: absolute;
  left: -9999px;
`

const ColorPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: #f6f8fa;
  border-radius: 6px;
  border: 1px solid #d0d7de;
`

const ColorPreview = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  background: ${props => props.color};
  border-radius: 6px;
  border: 2px solid #d0d7de;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
`

const ColorDetails = styled.div`
  flex: 1;
`

const ColorLabel = styled.div`
  font-size: 11px;
  color: #656d76;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ColorValue = styled.div`
  font-size: 14px;
  color: #24292f;
  font-family: 'Monaco', 'Consolas', monospace;
`
export {
  PanelContainer,
  PanelHeader,
  HeaderTitle,
  PropertiesContent,
  PropertySection,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionContent,
  PropertyGroup,
  Label,
  Input,
  TextArea,
  ColorInput,
  RangeInput,
  RangeValue,
  Button,
  EmptyState,
  EmptyStateIcon,
  FlexRow,
  Select,
  Checkbox,
  InfoText,
  FileInputWrapper,
  FileInputLabel,
  HiddenFileInput,
  ColorPickerWrapper,
  ColorPreview,
  ColorDetails,
  ColorLabel,
  ColorValue,
}
